import { useId } from "react";
import { useQuery } from "@tanstack/react-query";
import { Select } from "@/components/Selects/select";
import { CreatableSelect } from "@/components/Selects/select";
import { useTRPC } from "@/trpc";
import { STATES } from "@/consts/states";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import { PROFILE_SELECT_CLASS_NAMES } from "@/components/Selects/profile-select-styles";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { ProfessionalDetailsValue } from "./provider-professional-details-form";

const STATE_OPTIONS = STATES.map(([label, value]) => ({ label, value }));
type Choice = { label: string; value: string };
type PracticeDetails = Pick<
  ProfessionalDetailsValue,
  | "practiceSetting"
  | "primaryPracticeState"
  | "additionalPracticeStates"
  | "languages"
>;

function selectedChoice(value: string, options: Choice[]): Choice | null {
  if (!value) {
    return null;
  }
  return (
    options.find((option) => option.value === value) ?? { value, label: value }
  );
}

export function ProviderPracticeDetailsFields({
  value,
  disabled,
  onChange,
}: {
  value: PracticeDetails;
  disabled: boolean;
  onChange: (patch: Partial<PracticeDetails>) => void;
}) {
  const id = useId();
  const trpc = useTRPC();
  const settings = useQuery(trpc.facility.practiceSettings.queryOptions());
  const settingOptions = (settings.data?.practiceSettings ?? []).map(
    (setting) => ({ value: setting.displayText, label: setting.displayText }),
  );
  // The native profile stores the readable setting. Recognize an older ID for
  // display without rewriting it until the provider chooses an option.
  const savedSetting = settings.data?.practiceSettings.find(
    (setting) =>
      setting.id === value.practiceSetting ||
      setting.displayText === value.practiceSetting,
  );
  return (
    <>
      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor={`${id}-setting`} className={PROFILE_FORM_STYLES.label}>
          Practice setting
        </Label>
        <Select<Choice>
          inputId={`${id}-setting`}
          value={selectedChoice(
            savedSetting?.displayText ?? value.practiceSetting,
            settingOptions,
          )}
          options={settingOptions}
          isDisabled={
            disabled ||
            settings.isPending ||
            (settings.isError && !settings.data)
          }
          isLoading={settings.isPending}
          isClearable
          menuPlacement="auto"
          classNames={PROFILE_SELECT_CLASS_NAMES}
          placeholder="Select practice setting"
          onChange={(selection) =>
            onChange({ practiceSetting: selection?.value ?? "" })
          }
        />
        {settings.isError && (
          <p role="alert" className="text-xs text-red-700">
            Could not load practice settings.{" "}
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 text-xs"
              disabled={disabled || settings.isFetching}
              onClick={() => void settings.refetch()}
            >
              Try again
            </Button>
          </p>
        )}
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label
          htmlFor={`${id}-primary-state`}
          className={PROFILE_FORM_STYLES.label}
        >
          Primary practice state
        </Label>
        <Select<Choice>
          inputId={`${id}-primary-state`}
          value={selectedChoice(value.primaryPracticeState, STATE_OPTIONS)}
          options={STATE_OPTIONS}
          isDisabled={disabled}
          isClearable
          menuPlacement="auto"
          classNames={PROFILE_SELECT_CLASS_NAMES}
          placeholder="Select state"
          onChange={(selection) =>
            onChange({ primaryPracticeState: selection?.value ?? "" })
          }
        />
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label
          htmlFor={`${id}-additional-states`}
          className={PROFILE_FORM_STYLES.label}
        >
          Additional practice states
        </Label>
        <Select<Choice, true>
          inputId={`${id}-additional-states`}
          value={value.additionalPracticeStates.map(
            (state) => selectedChoice(state, STATE_OPTIONS)!,
          )}
          options={STATE_OPTIONS}
          isMulti
          isDisabled={disabled}
          isClearable
          menuPlacement="auto"
          classNames={PROFILE_SELECT_CLASS_NAMES}
          placeholder="Select states"
          onChange={(selections) =>
            onChange({
              additionalPracticeStates: selections.map(
                (selection) => selection.value,
              ),
            })
          }
        />
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label
          htmlFor={`${id}-languages`}
          className={PROFILE_FORM_STYLES.label}
        >
          Languages
        </Label>
        <CreatableSelect<Choice, true>
          inputId={`${id}-languages`}
          value={value.languages.map((language) => ({
            value: language,
            label: language,
          }))}
          isMulti
          isDisabled={disabled}
          isClearable
          menuPlacement="auto"
          classNames={PROFILE_SELECT_CLASS_NAMES}
          placeholder="Type a language and press Enter"
          formatCreateLabel={(language) => `Add ${language}`}
          onChange={(selections) =>
            onChange({
              languages: selections.map((selection) => selection.value),
            })
          }
        />
      </div>
    </>
  );
}
